import React, { Component } from 'react';
import { connect } from 'react-redux';
import { setQRCodePoint, setQRCodeSize, setCaptionPoint } from 'admin/actions/qr-designer';
const qrjs = require('qr.js');
import { QRCODE_MIN_SIZE, CANVAS_WIDTH, CANVAS_HEIGHT } from 'admin/constants/qr-designer';

const thickness = 0.5;
const exampleText = '例）ポケペイ商店';
const exampleQRValue = 'https://pay.pocket-change.jp/bills/c4f4630a-995f-4103-9867-51887cbdc917';

const subtract = (pos1, pos2) => {
  const [x1, y1] = pos1;
  const [x2, y2] = pos2;
  return [x1 - x2, y1 - y2];
}

const mousePosition = (e) => {
  const canvas = e.target;
  const { left, top } = canvas.getBoundingClientRect();
  const x = e.clientX - left;
  const y = e.clientY - top;
  return [x, y];
}

class CanvasObject {
  constructor(x, y) {
    this._x = x;
    this._y = y;
    this.active = false;
    this.resizerRect = null;
    this.draggable = false;
  }

  get x() {
    return this._x;
  }

  get y() {
    return this._y;
  }

  getWidth() {
    new Error('getWidth is not implemented');
  }

  getHeight() {
    new Error('getHeight is not implemented');
  }

  moved() {
    return null;
  }

  resized() {
    return null;
  }

  isMouseOver(x, y) {
    return (
      this.x <= x && x <= this.x + this.getWidth() &&
      this.y <= y && y <= this.y + this.getHeight()
    );
  }

  isDraggable() {
    return this.draggable;
  }

  move(x, y) {
    this._x = x;
    this._y = y;
  }

  resize(width, height) {
    new Error('resize is not implemented');
  }

  drawActivityMark(ctx, margin = 0) {
    ctx.strokeStyle = 'cyan';
    ctx.lineWidth = 2;
    ctx.strokeRect(
      this.x - margin,
      this.y - margin,
      this.getWidth() + margin * 2,
      this.getHeight() + margin * 2
    );
  }

  drawResizer(ctx, margin = 0) {
    ctx.fillStyle = 'cyan';
    ctx.fillRect(
      this.x + this.getWidth() + margin * 2 - 6,
      this.y + this.getHeight() + margin * 2 - 6,
      12,
      12
    );

    this.resizerRect = [
      this.x + this.getWidth() + margin * 2 - 4,
      this.y + this.getHeight() + margin * 2 - 4,
      8,
      8
    ];

    ctx.fillStyle = 'white';
    ctx.fillRect(...this.resizerRect);
  }

  isMouseOverResizable(x, y) {
    if (this.resizerRect === null) return false;
    const [x1, y1, w, h] = this.resizerRect;
    return x1 <= x && x <= x1 + w && y1 <= y && y <= y1 + h;
  }

  movedPosition() {
    return [this.x, this.y];
  }
}

class ImageObject extends CanvasObject {
  constructor(image, x, y) {
    super(x, y);
    this.image = image;
  }

  getWidth() {
    return this.image.width;
  }

  getHeight() {
    return this.image.height;
  }

  draw({ ctx }) {
    ctx.drawImage(
      this.image,
      this.x,
      this.y
    );
  }
}

class BackgroundImage extends ImageObject {
  constructor(image) {
    super(image, 0, 0);
  }

  draw({ canvas, ctx }) {
    ctx.drawImage(
      this.image,
      this.x,
      this.y,
      canvas.width,
      canvas.height
    );
  }
}

class QRCodeImage extends CanvasObject {
  constructor(x, y, size, active, value) {
    super(x, y);
    this.size = size;
    this.active = active;
    this.canvas = this.createImage(value);
    this.value = value;
    this.draggable = true;
  }

  createImage(value) {
    const qrcode = qrjs(value, { errorCorrectLevel: 1 });
    const matrix = qrcode.modules;
    const modsize = 100;
    const n = matrix.length;
    const size = modsize * (n + 8);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    context.fillStyle = '#fff';
    context.fillRect(0, 0, size, size);
    context.fillStyle = '#000';
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if (matrix[i][j]) {
          context.fillRect(j * modsize + 4 * modsize, i * modsize + 4 * modsize, modsize, modsize);
        }
      }
    }
    return canvas;
  }

  getWidth() {
    return this.size;
  }

  getHeight() {
    return this.size;
  }

  resize(size) {
    this.size = size;
  }

  moved() {
    const [x, y] = this.movedPosition();
    return setQRCodePoint(x, y);
  }

  resized() {
    return setQRCodeSize(this.getWidth());
  }

  draw({ ctx }) {
    ctx.drawImage(
      this.canvas,
      this.x,
      this.y,
      this.size,
      this.size
    );
    if (this.active) {
      this.drawActivityMark(ctx);
      this.drawResizer(ctx);
    }
  }

  recomputeValue(value) {
    if (this.value !== value) {
      this.canvas = this.createImage(value);
      this.value = value;
    }
  }
}

class TextObject extends CanvasObject {
  constructor({ x, y, text, size, color, align, active, bold, italic }) {
    super(x, y);
    this.text = text;
    this.active = active;
    this.width = 0;
    this.height = size;
    this.color = color;
    this.align = align;
    this.bold = bold;
    this.italic = italic;
    this.draggable = true;
  }

  reinitialize({ x, y, text, size, color, align, bold, italic }) {
    this._x = x;
    this._y = y;
    this.text = text;
    this.width = 0;
    this.height = size;
    this.color = color;
    this.align = align;
    this.bold = bold;
    this.italic = italic;
  }

  get x() {
    if (this.align === 'center') {
      return Math.round(this._x - this.width / 2);
    } else {
      return this._x;
    }
  }

  move(x, y) {
    if (this.align === 'center') {
      this._x = Math.round(x + this.width / 2);
    } else {
      this._x = x;
    }
    this._y = y;
  }

  getWidth() {
    return this.width;
  }

  getHeight() {
    return this.height;
  }

  draw({ ctx }) {
    ctx.font = `${this.italic ? 'italic' : ''} ${this.bold ? 'bold' : ''} ${this.height}px Noto sans CJK JP`;
    ctx.textBaseline = 'top';
    this.width = ctx.measureText(this.text).width;
    ctx.fillStyle = this.color;
    ctx.fillText(this.text, this.x, this.y);

    if (this.active) {
      this.drawActivityMark(ctx, 2);
    }
  }

  movedPosition() {
    return [this._x, this._y];
  }

  moved() {
    const [x, y] = this.movedPosition();
    return setCaptionPoint(x, y);
  }
}

class DefaultBackground extends CanvasObject {
  draw({ ctx, canvas }) {
    ctx.fillStyle = '#444444';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
}

class Dragging {
  constructor(mouseX, mouseY, canvasObject, kind) {
    this.offsetPosition = [mouseX - canvasObject.x, mouseY - canvasObject.y];
    this.startMousePosition = [mouseX, mouseY];
    this.canvasObject = canvasObject;
    this.oldWidth = this.canvasObject.getWidth();
    this.oldHeight = this.canvasObject.getHeight();
    this.kind = kind;
  }

  getMovedPosition(position) {
    return subtract(position, this.offsetPosition);
  }

  getResizedSize(position) {
    const diffSize = subtract(position, this.startMousePosition);
    return [this.oldWidth + diffSize[0], this.oldHeight + diffSize[1]];
  }
}

export class CanvasHelper {
  constructor() {
    this.canvas = null;
    const canvasObjects = new Map();
    canvasObjects.set('none', null);
    canvasObjects.set('background', null);
    canvasObjects.set('qr', null);
    canvasObjects.set('caption', null);
    this.canvasObjects = canvasObjects;
    this._onload = null;
  }

  getContext() {
    return this.canvas.getContext('2d');
  }

  clear() {
    const ctx = this.getContext();
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  drawImages() {
    if (this.canvas === null) {
      return;
    }
    const params = { ctx: this.getContext(), canvas: this.canvas };
    for (let [, canvasObject] of this.canvasObjects) {
      if (canvasObject) {
        canvasObject.draw(params);
      }
    }
  }

  drawImagesWhenOnload = () => {
    this.drawImages();
    if (this._onload) {
      this._onload();
    }
  }

  openImage(src) {
    const image = new Image();
    image.crossOrigin = "Anonymous";
    image.src = src;
    image.onload = this.drawImagesWhenOnload;
    return image;
  }

  withDraw(body) {
    return new Promise((resolve) => {
      this._onload = resolve;
      body();
    });
  }

  getDraggableObjects() {
    const objects = [];
    for (let [, canvasObject] of this.canvasObjects) {
      if (canvasObject !== null && canvasObject.isDraggable()) {
        objects.push(canvasObject);
      }
    }
    return objects;
  }

  findResizer = (x, y) => {
    for (let canvasObject of this.getDraggableObjects()) {
      if (canvasObject.isMouseOverResizable(x, y) && canvasObject.active) {
        return canvasObject;
      }
    }
    return null;
  }

  findActiveObject = (x, y) => {
    for (let canvasObject of this.getDraggableObjects()) {
      if (canvasObject.isMouseOver(x, y) && canvasObject.active) {
        return canvasObject;
      }
    }
    return null;
  }

  findInactiveObject = (x, y) => {
    for (let canvasObject of this.getDraggableObjects()) {
      if (canvasObject.isMouseOver(x, y) && !canvasObject.active) {
        return canvasObject;
      }
    }
    return null;
  }

  changeActiveObject(canvasObject) {
    for (let canvasObject of this.getDraggableObjects()) {
      canvasObject.active = false;
    }
    canvasObject.active = true;
  }

  clearCanvasObjects() {
    this.canvasObjects.clear();
  }

  putCanvasObject(objectName, canvasObject) {
    this.canvasObjects.set(objectName, canvasObject);
  }

  putBackgroundImage(image) {
    this.putCanvasObject('none', new DefaultBackground());
    if (image) {
      this.putCanvasObject('background', new BackgroundImage(this.openImage(image)));
    }
  }

  putQRImage(qrcode, options = {}) {
    const {
      active = false,
      value = exampleQRValue,
      canvasSize = { width: CANVAS_WIDTH, height: CANVAS_HEIGHT },
    } = options;
    const { x, y, size } = qrcode;
    const obj = this.canvasObjects.get('qr');
    if (obj) {
      obj.move(
        x * canvasSize.width / CANVAS_WIDTH,
        y * canvasSize.height / CANVAS_HEIGHT
      );
      obj.resize(size * canvasSize.width / CANVAS_WIDTH);
      obj.recomputeValue(value);
    } else {
      this.putCanvasObject('qr',
        new QRCodeImage(
          x * canvasSize.width / CANVAS_WIDTH,
          y * canvasSize.height / CANVAS_HEIGHT,
          size * canvasSize.width / CANVAS_WIDTH,
          active,
          value
        )
      );
    }
  }

  putCaption(caption, options = {}) {
    const {
      active = false,
      text = exampleText,
      canvasSize = { width: CANVAS_WIDTH, height: CANVAS_HEIGHT },
    } = options;
    const obj = this.canvasObjects.get('caption');
    const params = {
      x: caption.x * canvasSize.width / CANVAS_WIDTH,
      y: caption.y * canvasSize.height / CANVAS_HEIGHT,
      text: text,
      size: caption.size * canvasSize.width / CANVAS_WIDTH,
      color: caption.color,
      align: caption.align,
      bold: caption.bold,
      italic: caption.italic,
    };
    if (obj) {
      obj.reinitialize(params);
    } else {
      this.putCanvasObject('caption', new TextObject({ active, ...params }));
    }
  }
}

class QRCanvas extends Component {
  constructor(props) {
    super(props);
    this.helper = this.props.helper || new CanvasHelper();
    this.dragging = null;
  }

  componentDidMount() {
    this.update();
    if (this.props.draggable) {
      const canvas = this.helper.canvas;
      canvas.addEventListener('mousedown', this.onDocumentMouseDown, false);
      canvas.addEventListener('mousemove', this.onDocumentMouseMove, false);
      canvas.addEventListener('mouseup', this.onDocumentMouseUp, false);
    }
  }

  componentWillUnmount() {
    if (this.props.draggable) {
      const canvas = this.helper.canvas;
      canvas.removeEventListener('mousedown', this.onDocumentMouseDown);
      canvas.removeEventListener('mousemove', this.onDocumentMouseMove);
      canvas.removeEventListener('mouseup', this.onDocumentMouseUp);
    }
  }

  componentDidUpdate() {
    this.update();
  }

  onDocumentMouseDown = e => {
    const [x, y] = mousePosition(e);
    let canvasObject;
    if (canvasObject = this.helper.findResizer(x, y)) {
      this.dragging = new Dragging(x, y, canvasObject, 'resize');
      return;
    }
    if (canvasObject = this.helper.findActiveObject(x, y)) {
      this.dragging = new Dragging(x, y, canvasObject, 'move');
      return;
    }
    if (canvasObject = this.helper.findInactiveObject(x, y)) {
      this.helper.changeActiveObject(canvasObject);
      this.dragging = new Dragging(x, y, canvasObject, 'move');
      this.helper.drawImages();
      return;
    }
  }

  onDocumentMouseMove = e => {
    if (this.dragging === null) return;
    switch (this.dragging.kind) {
      case 'move':
        this.onMoveEvent(e);
        break;
      case 'resize':
        this.onResizeEvent(e);
        break;
    }
  }

  onDocumentMouseUp = () => {
    if (this.dragging === null) return;
    let action = null;
    switch (this.dragging.kind) {
      case 'move':
        action = this.dragging.canvasObject.moved();
        break;
      case 'resize':
        action = this.dragging.canvasObject.resized();
        break;
    }
    if (action !== null) {
      this.props.dispatch(action);
    }
    this.helper.clear();
    this.helper.drawImages();
    this.dragging = null;
  }

  onMoveEvent = e => {
    const canvas = this.helper.canvas;
    const canvasObject = this.dragging.canvasObject;
    let [x, y] = this.dragging.getMovedPosition(mousePosition(e));
    let adjustX = false, adjustY = false;

    if (Math.abs((x + canvasObject.getWidth() / 2) - canvas.width / 2) < 10) {
      x = Math.round(canvas.width / 2 - canvasObject.getWidth() / 2);
      adjustX = true;
    }

    if (Math.abs((y + canvasObject.getHeight() / 2) - canvas.height / 2) < 10) {
      y = Math.round(canvas.height / 2 - canvasObject.getHeight() / 2);
      adjustY = true;
    }

    if (x < 0) {
      x = 0;
    }

    if (y < 0) {
      y = 0;
    }

    canvasObject.move(x, y);
    this.helper.clear();
    this.helper.drawImages();

    if (adjustX) {
      this.drawVerticalDynamicGuides();
    }

    if (adjustY) {
      this.drawHorizontalDynamicGuides();
    }
  }

  onResizeEvent = e => {
    const canvasObject = this.dragging.canvasObject;
    const [newWidth, newHeight] = this.dragging.getResizedSize(mousePosition(e));
    const size = Math.max(newWidth, newHeight);
    if (size >= QRCODE_MIN_SIZE) {
      canvasObject.resize(size);
      this.helper.clear();
      this.helper.drawImages();
    }
  }

  drawVerticalDynamicGuides() {
    const canvas = this.helper.canvas;
    const ctx = this.helper.getContext();
    ctx.fillStyle = "black";
    ctx.fillRect(canvas.width / 2 - 1, 0, thickness, canvas.height);
  }

  drawHorizontalDynamicGuides() {
    const canvas = this.helper.canvas;
    const ctx = this.helper.getContext();
    ctx.fillStyle = "black";
    ctx.fillRect(0, canvas.height / 2 - 1, canvas.width, thickness);
  }

  update() {
    this.helper.putBackgroundImage(this.props.background.image);
    this.props.draw(this.helper);
  }

  render() {
    return (
      <canvas
        width={this.props.background.width}
        height={this.props.background.height}
        ref={(e) => this.helper.canvas = e}>
      </canvas>
    );
  }
}

export default connect(null, null)(QRCanvas);
